import { Slot } from '@radix-ui/react-slot';
import { forwardRef } from 'react';

import { usePopupContext } from '../popup';

import type { ForwardedRef, ReactElement } from 'react';

import type { PopupCloseProps } from './popup-close.types';

/**
 * The `PopupClose` component is used to close the pop-up interface.
 * It typically contains a button or other interactive element that, when clicked, triggers the closing of the pop-up.
 *
 * ### Usage
 *
 * ```tsx
 * import { Popup, PopupTrigger, PopupContent PopupClose } from '@bloomreach/react-banana-ui';
 *
 * export default function MyCustomComponent() {
 *   return (
 *     <Popup>
 *       <PopupTrigger>Trigger</PopupTrigger>
 *       <PopupContent>Content</PopupContent>
 *       <PopupClose>Close</PopupClose>
 *     </Popup>
 *   );
 * }
 * ```
 */
const PopupClose = forwardRef(
  (props: PopupCloseProps, forwardedRef: ForwardedRef<HTMLButtonElement> | null): ReactElement => {
    const {
      children,
      ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
    } = props;

    const popupContext = usePopupContext();
    if (popupContext == null) {
      throw new Error('Popup components must be wrapped in <Popup />');
    }

    const { setIsOpen } = popupContext;

    return (
      <Slot
        onClick={() => {
          setIsOpen(false);
        }}
        ref={forwardedRef}
        {...rest}
      >
        {children}
      </Slot>
    );
  },
);

PopupClose.displayName = 'PopupClose';

export default PopupClose;
